import React, { useEffect, useRef, useState } from "react";
import { Swords, MessageSquare, Send, Volume2, RotateCcw, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { api, audioUrlForKey } from "../lib/api";

const STATUS = {
  ACTIVE: "bg-emerald-500/15 text-emerald-300 border-emerald-400/40",
  WARNED: "bg-amber-500/15 text-amber-300 border-amber-400/40",
  FIRED: "bg-rose-500/15 text-rose-300 border-rose-400/40",
};
const money = (v) => `${v < 0 ? "-" : ""}$${Math.abs(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const HerculesTalk = ({ onClose }) => {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const sid = useRef(null);
  const send = async () => {
    const m = text.trim();
    if (!m || busy) return;
    setText(""); setBusy(true);
    setMsgs((x) => [...x, { who: "you", text: m }]);
    try {
      const r = (await api.post("/hercules/chat", { message: m, session_id: sid.current })).data;
      sid.current = r.session_id;
      setMsgs((x) => [...x, { who: "herc", text: r.reply_text, audio: r.audio_key }]);
    } catch (e) { toast.error("Hercules 2.0 didn't answer"); }
    setBusy(false);
  };
  return (
    <div className="mt-3 border-t border-[#F5C45122] pt-3 space-y-2" data-testid="hercules-chat">
      <div className="flex justify-between items-center">
        <p className="nx-mono text-[10px] tracking-widest text-[#F5C451]">TALK TO HERCULES</p>
        <button onClick={onClose} data-testid="hercules-chat-close" className="text-cyan-200/50"><X className="w-4 h-4" /></button>
      </div>
      <div className="max-h-56 overflow-y-auto space-y-2" data-testid="hercules-chat-log">
        {msgs.map((m, i) => (
          <div key={i} className={`text-sm rounded-lg px-3 py-2 ${m.who === "you" ? "bg-cyan-400/10 text-cyan-50 ml-8" : "bg-[#F5C4510f] text-amber-50 mr-8"}`} data-testid={`hercules-msg-${i}`}>
            {m.text}
            {m.audio && <button onClick={() => new Audio(audioUrlForKey(m.audio)).play()} data-testid={`hercules-hear-${i}`} className="ml-2 text-[#F5C451] align-middle"><Volume2 className="w-3.5 h-3.5 inline" /></button>}
          </div>
        ))}
        {busy && <p className="text-xs text-cyan-200/50 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Hercules 2.0 is answering…</p>}
      </div>
      <div className="flex gap-2">
        <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Hold him accountable…"
          data-testid="hercules-chat-input" className="flex-1 rounded-lg bg-[#0b1220] border border-white/10 px-3 py-2 text-sm text-cyan-50" />
        <button onClick={send} disabled={busy} data-testid="hercules-chat-send" className="px-3 rounded-lg bg-[#F5C451] text-[#030712]"><Send className="w-4 h-4" /></button>
      </div>
    </div>
  );
};

export const HerculesCard = () => {
  const [s, setS] = useState(null);
  const [edit, setEdit] = useState({});
  const [talk, setTalk] = useState(false);
  const load = () => api.get("/hercules/accountability").then((r) => { setS(r.data); setEdit({ max_loss: r.data.max_loss }); }).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 120000); return () => clearInterval(t); }, []);
  if (!s) return null;
  const save = async () => {
    const r = (await api.post("/hercules/accountability/settings", { max_loss: Number(edit.max_loss) })).data;
    setS(r); toast.success("Hercules 2.0 limits saved");
  };
  const rehire = async () => { setS((await api.post("/hercules/accountability/rehire")).data); toast.success("Hercules 2.0 rehired — strikes reset"); };
  return (
    <div className="bg-[#060b18] border border-[#F5C45144] rounded-2xl p-4" data-testid="hercules-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#F5C4511a] border border-[#F5C45166] flex items-center justify-center"><Swords className="w-5 h-5 text-[#F5C451]" /></div>
          <div>
            <p className="text-amber-50 font-bold">Hercules 2.0 <span className="text-xs text-cyan-200/50 font-normal">· Floor Enforcer</span></p>
            <p className="text-xs text-cyan-200/60" data-testid="hercules-pnl">Today {money(s.pnl)} · max loss {money(s.max_loss)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-1" data-testid="hercules-strikes" title={`${s.strikes}/3 strikes (7 days)`}>
            {[0, 1, 2].map((i) => <span key={i} className={`w-3 h-3 rounded-full border ${i < s.strikes ? "bg-rose-500 border-rose-400" : "border-cyan-200/30"}`} />)}
          </div>
          <span className={`px-2 py-0.5 rounded-full border nx-mono text-[10px] tracking-widest ${STATUS[s.status]}`} data-testid="hercules-status">{s.status}</span>
        </div>
      </div>
      <div className="flex items-end gap-2 mt-3 flex-wrap">
        <label className="text-[10px] text-cyan-200/50">Max loss $
          <input type="number" value={edit.max_loss ?? ""} onChange={(e) => setEdit({ ...edit, max_loss: e.target.value })} data-testid="hercules-maxloss-input"
            className="block w-24 mt-0.5 rounded bg-[#0b1220] border border-white/10 px-2 py-1 text-cyan-50 text-xs" /></label>
        <button onClick={save} data-testid="hercules-limits-save" className="px-3 py-1.5 rounded-lg border border-cyan-300/30 text-cyan-100 text-xs">Save</button>
        <div className="ml-auto flex gap-2">
          {(s.status !== "ACTIVE" || s.strikes > 0) && <button onClick={rehire} data-testid="hercules-rehire-btn" className="px-3 py-1.5 rounded-lg border border-rose-400/40 text-rose-200 text-xs flex items-center gap-1"><RotateCcw className="w-3.5 h-3.5" /> Rehire</button>}
          <button onClick={() => setTalk((v) => !v)} data-testid="hercules-talk-btn" className="px-3 py-1.5 rounded-lg bg-[#F5C451] text-[#030712] text-xs font-bold flex items-center gap-1"><MessageSquare className="w-3.5 h-3.5" /> Talk</button>
        </div>
      </div>
      {talk && <HerculesTalk onClose={() => { setTalk(false); load(); }} />}
    </div>
  );
};
